/**
 * annotations.js — 圖形溝通用的標註層（雲狀圖／箭頭／文字／手繪／直線／方框）。
 *
 * 這一層是「覆蓋在圖面上的一張 SVG」，**完全不動原圖**：
 *   - 2D：覆蓋層與圖面共用同一個 `viewBox`，所以標註存的是**圖面座標**
 *         （換算成 DWG 模型座標只需要一次線性映射 → 之後要匯出成 DWG 註解層
 *          或 PDF 標註都用同一份資料）。
 *   - 3D：三維畫面沒有「圖面座標」可言，所以存的是**正規化畫面座標**
 *         （0..1）＋ 當時的**視點**（相機）。重開時如果視點不同，標註仍然在，
 *          但會標成「視點不同」，點一下可以回到當初的視角。
 *
 * 三種座標系在同一個類別裡處理，靠的是 `space`：
 *   'drawing' → viewBox 跟著圖面跑，單位是圖面單位
 *   'screen'  → viewBox 固定 `0 0 1 1`，單位是畫面比例
 * 指標座標一律用 `getScreenCTM().inverse()` 換算，兩種都對。
 *
 * 線寬用 `vector-effect="non-scaling-stroke"` → 不論 zoom 幾倍都是 2px；
 * 文字沒有這種能力，所以字級由「一個像素等於多少座標單位」推算，並在 viewBox
 * 變動時（rAF 節流）重畫。
 */

const NS = 'http://www.w3.org/2000/svg';

/** 標註型別的中文名（給議題清單與提示用）。 */
export const TYPE_LABEL = {
  cloud: '雲狀圖', arrow: '箭頭', text: '文字', ink: '手繪', line: '直線', rect: '方框',
};

const el = (name, attrs = {}) => {
  const n = document.createElementNS(NS, name);
  for (const [k, v] of Object.entries(attrs)) if (v != null) n.setAttribute(k, String(v));
  return n;
};

const uid = () => Math.random().toString(36).slice(2, 9);
const clone = (v) => JSON.parse(JSON.stringify(v));

/**
 * 修訂雲的路徑。
 *
 * AutoCAD 的修訂雲本質是「帶 bulge 的聚合線」—— 每一段都是一個鼓起來的弧。
 * 這裡用同樣的做法：把每一條邊拆成兩個圓弧（中點為界），看起來就是雲。
 * 之所以不畫成一堆短線段：這樣在極端 zoom 下仍然是平滑的，而且之後要匯出成
 * DWG 的 `OdDbPolyline`（setBulgeAt）時，控制點就是這裡的點。
 */
export function cloudPath(pts) {
  if (pts.length < 3) return '';
  let d = '';
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    // 半徑取邊長的一定比例，太小的邊不要再縮（否則會出現尖刺）
    const r = Math.max(len * 0.62, 1e-9);
    const sweep = 1;
    if (i === 0) d += `M ${a[0]} ${a[1]} `;
    // 兩個弧：a → 中點 → b，兩段都往同一側鼓
    const mx = a[0] + dx / 2;
    const my = a[1] + dy / 2;
    d += `A ${r} ${r} 0 0 ${sweep} ${mx} ${my} `;
    d += `A ${r} ${r} 0 0 ${sweep} ${b[0]} ${b[1]} `;
  }
  return d + 'Z';
}

/** 箭頭：主線 + 箭頭三角（在座標系裡算，所以箭頭大小是圖面單位）。 */
export function arrowPath(pts, headLen) {
  if (pts.length < 2) return { line: '', head: '' };
  const [a, b] = [pts[0], pts[pts.length - 1]];
  const line = `M ${a[0]} ${a[1]} L ${b[0]} ${b[1]}`;
  const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
  const h = headLen;
  const spread = 0.42;
  const p1 = [b[0] - h * Math.cos(ang - spread), b[1] - h * Math.sin(ang - spread)];
  const p2 = [b[0] - h * Math.cos(ang + spread), b[1] - h * Math.sin(ang + spread)];
  return { line, head: `${b[0]},${b[1]} ${p1[0]},${p1[1]} ${p2[0]},${p2[1]}` };
}

export class AnnotationLayer {
  /**
   * @param {object} opts
   * @param {HTMLElement} opts.host 要覆蓋的容器（`#canvas` 或 `#view3d`）
   * @param {'drawing'|'screen'} opts.space
   * @param {string} [opts.color] 預設顏色
   */
  constructor({ host, space = 'drawing', color = '#e5484d' }) {
    this.host = host;
    this.space = space;
    this.color = color;
    this.items = [];
    this.tool = null;          // null = 檢視模式
    this.selected = null;
    this.visible = true;
    this.onchange = null;      // 內容變更（呼叫端負責存檔）
    this.onselect = null;
    this.onstatus = null;      // 給 UI 的提示文字

    this.active = false;
    this.svg = el('svg', { class: 'ann-layer' });
    this.g = el('g', { class: 'ann-shapes' });
    this.svg.append(this.g);
    this.host.append(this.svg);
    this.setViewBox(space === 'screen' ? { x: 0, y: 0, w: 1, h: 1 } : { x: 0, y: 0, w: 1, h: 1 });
    this._draft = null;
    this._pending = null;      // 正在累積點數的標註（雲狀圖／手繪）
    this._raf = 0;
    this._bind();
    // 線寬與字級是「螢幕像素換算成使用者單位」，視窗改變大小就要重畫
    this._onResize = () => this.setViewBox(this._vb);
    window.addEventListener('resize', this._onResize);
  }

  /* ── 座標系 ─────────────────────────────────────────────── */

  /**
   * 換宿主（2D ⇄ 3D）。
   *
   * 刻意**只有一個實例**、用搬移的方式換宿主，而不是每個檢視各建一個：
   * 兩份實例會各自掛一個 window keydown，按 Delete 就會刪到兩邊的標註。
   */
  attach(host, space) {
    this.host = host;
    host.append(this.svg);
    this.setSpace(space);
    return this;
  }

  setSpace(space) {
    this.space = space;
    this.setViewBox(space === 'screen' ? { x: 0, y: 0, w: 1, h: 1 } : this._vb ?? { x: 0, y: 0, w: 1, h: 1 });
  }

  setViewBox(vb) {
    if (!vb) return;
    this._vb = vb;
    this.svg.setAttribute('viewBox', `${vb.x} ${vb.y} ${vb.w} ${vb.h}`);
    this.svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    // 字級／箭頭大小是「圖面單位」，viewBox 一變就要重算（rAF 節流）
    if (!this._raf) {
      this._raf = requestAnimationFrame(() => {
        this._raf = 0;
        if (this._vb.w > 0) this.render();
      });
    }
  }

  /**
   * 一個螢幕像素等於多少座標單位（用來讓文字／徽章在畫面上固定大小）。
   *
   * **一定要夾住**：如果量到的寬度是 0（元素還沒 layout、或剛被搬動），
   * `vb.w / 1` 會變成「1 個像素 = 整個 viewBox」—— 半徑 9 的徽章圓就變成
   * 半徑 9 的**巨大紅圓**蓋滿整個畫面（實測在 3D 標註真的發生過）。
   * 所以量不到就沿用上次量到的值，沒有上次值就用 1000px 這種合理預設。
   */
  _unitsPerPx() {
    const w = this.svg?.getBoundingClientRect?.().width
      || this.host?.getBoundingClientRect?.().width
      || 0;
    if (w > 4) this._goodWidth = w;
    const px = this._goodWidth || 1000;
    return (this._vb?.w ?? 1) / px;
  }

  /** 螢幕座標 → 這一層的座標（2D 是圖面單位、3D 是 0..1）。 */
  toLocal(clientX, clientY) {
    const m = this.svg.getScreenCTM();
    if (!m) return null;
    const p = this.svg.createSVGPoint();
    p.x = clientX;
    p.y = clientY;
    const q = p.matrixTransform(m.inverse());
    return [q.x, q.y];
  }

  /* ── 資料 ───────────────────────────────────────────────── */

  setItems(items) {
    this.items = (items ?? []).map((i) => {
      const c = clone(i);
      c.space = c.space ?? this.space;
      return c;
    });
    this.render();
  }

  getItems() {
    return this.items.map(clone);
  }

  /**
   * 目前的視點（由呼叫端在相機變動時餵進來）—— 用來判斷「這則 3D 標註是不是
   * 在這個視角畫的」。
   *
   * 為什麼需要：3D 標註存的是**畫面座標**，如果使用者轉到別的角度，那些標註
   * 仍然會黏在原本的螢幕位置上，看起來就像浮在模型外面（實測就是這樣被回報
   * 「功能有點異常」）。所以視點不同時要**畫成虛線並標示**，讓使用者知道
   * 那不是現在的視角，並提供「回到這個視角」。
   */
  setCurrentViewpoint(vp) {
    this.viewpoint = vp ?? null;
    this.render();
  }

  /** 這則標註的視點與目前視點是否相符（2D 或沒有視點的一律算相符）。 */
  viewpointMatches(it) {
    if (it?.space !== 'screen' || !it.viewpoint) return true;
    const cur = this.viewpoint;
    if (!cur) return true;
    const vp = it.viewpoint;
    if (Boolean(vp.ortho) !== Boolean(cur.ortho)) return false;
    const d = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
    // 以「視點距離」為尺度：遠近不同的圖不該用同一個絕對容差
    const scale = Math.max(1e-6, d(vp.pos, vp.target));
    return d(vp.pos, cur.pos) / scale < 0.05 && d(vp.target, cur.target) / scale < 0.05;
  }

  /** 目前有哪些標註是「別的視角」畫的（給清單顯示與提示用）。 */
  staleIds() {
    return this.items.filter((i) => !this.viewpointMatches(i)).map((i) => i.id);
  }

  setVisible(on) {
    this.visible = Boolean(on);
    this.svg.style.display = this.visible ? '' : 'none';
  }

  /**
   * 標註模式開／關。
   *
   * 兩種狀態的指標行為**必須分開**：
   *   - `drawing`（選了工具）：整層接管，畫圖用。
   *   - `picking`（標註模式但沒選工具）：只有既有標註接得到點擊（選取），
   *     空白處仍然可以平移圖面。
   * 非標註模式時整層 `pointer-events: none` —— 否則標註會蓋住底下的 UI
   * （實測把 3D 的視角按鈕擋住，按都按不到）。
   */
  setActive(on) {
    this.active = Boolean(on);
    this._syncPointerMode();
  }

  _syncPointerMode() {
    if (!this.svg) return;
    this.svg.classList.toggle('drawing', Boolean(this.active && this.tool));
    this.svg.classList.toggle('picking', Boolean(this.active && !this.tool));
  }

  setTool(tool) {
    this.tool = tool;
    this._pending = null;
    if (!tool) this._draft = null;
    this._syncPointerMode();
    this.render();
    this._status(tool ? `${TYPE_LABEL[tool] ?? tool}：` + this._hintFor(tool) : '');
  }

  _hintFor(tool) {
    if (tool === 'cloud') return '逐點點出範圍，雙擊或 Enter 完成（至少 3 點）';
    if (tool === 'ink') return '按住拖曳手繪';
    if (tool === 'text') return '點一下放置文字';
    return '按住拖曳，放開完成';
  }

  select(id) {
    this.selected = id;
    this.render();
    this.onselect?.(id);
  }

  update(id, patch) {
    const it = this.items.find((i) => i.id === id);
    if (!it) return null;
    Object.assign(it, patch);
    this.render();
    this.onchange?.();
    return it;
  }

  delete(id) {
    const n = this.items.length;
    this.items = this.items.filter((i) => i.id !== id);
    if (this.selected === id) this.selected = null;
    this.render();
    if (this.items.length !== n) this.onchange?.();
  }

  clear() {
    this.items = [];
    this.selected = null;
    this.render();
  }

  /** 標註的包圍盒（給「帶我去看這則」用）。 */
  bbox(id) {
    const it = this.items.find((i) => i.id === id);
    if (!it?.points?.length) return null;
    const xs = it.points.map((p) => p[0]);
    const ys = it.points.map((p) => p[1]);
    return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
  }

  _status(t) { this.onstatus?.(t); }

  /* ── 繪製 ───────────────────────────────────────────────── */

  render() {
    if (!this.g) return;
    const upp = this._unitsPerPx();
    const font = 13 * upp;
    const head = 11 * upp;
    this.g.textContent = '';
    const draw = (it, draft = false) => {
      // 線寬用**使用者單位**換算（upp = 一個螢幕像素等於多少單位），
      // 讓它在畫面上固定是 2px。**不要用 `vector-effect="non-scaling-stroke"`**：
      // Blink 的命中測試會用未縮放的寬度，在 1×1 的 3D viewBox 裡那條
      // 「2 單位寬」的線等於兩千像素寬的隱形命中帶，會蓋住整個 UI。
      const stale = !draft && !this.viewpointMatches(it);
      // 類別字串**一次算完**：以前是先建 g 再 setAttribute('class', …) 蓋掉，
      // 結果「被選取的那一則」會失去 ann-stale（畫面上就沒有虛線了）。
      const cls = `ann ann-${it.type}${draft ? ' draft' : ''}${stale ? ' ann-stale' : ''}`
        + `${this.selected === it.id && !draft ? ' ann-selected' : ''}`;
      // 描邊寬度與虛線長度都**必須**用 upp 換算成使用者單位。
      // 教訓：這些屬性若寫在 CSS（例如 `.ann-selected { stroke-width: 3.5 }`），
      // 單位是「使用者單位」—— 在 3D 的 1×1 viewBox 裡 3.5 等於 3663px 寬，
      // 畫面會變成一大片紅色，而且那條超寬描邊的**命中區域**會蓋住整個 UI
      // （按鈕全部按不到）。這兩個症狀同一個原因，實際踩過。
      const sel = this.selected === it.id && !draft;
      const g = el('g', {
        class: cls,
        'data-id': it.id,
        stroke: it.color,
        fill: 'none',
        'stroke-width': ((it.width ?? 2) + (sel ? 1.5 : 0)) * upp,
        'stroke-dasharray': stale ? `${6 * upp} ${4 * upp}` : (draft ? `${4 * upp} ${3 * upp}` : null),
        'stroke-linejoin': 'round',
        'stroke-linecap': 'round',
      });
      const pts = it.points ?? [];
      if (it.type === 'cloud') {
        g.append(el('path', { d: cloudPath(pts) }));
      } else if (it.type === 'arrow') {
        const { line, head: hd } = arrowPath(pts, Math.max(head, 1e-9));
        g.append(el('path', { d: line }));
        if (hd) g.append(el('polygon', { points: hd, fill: it.color, stroke: 'none' }));
      } else if (it.type === 'line') {
        if (pts.length >= 2) g.append(el('path', { d: `M ${pts[0][0]} ${pts[0][1]} L ${pts[1][0]} ${pts[1][1]}` }));
      } else if (it.type === 'rect') {
        if (pts.length >= 2) {
          g.append(el('rect', {
            x: Math.min(pts[0][0], pts[1][0]), y: Math.min(pts[0][1], pts[1][1]),
            width: Math.abs(pts[1][0] - pts[0][0]), height: Math.abs(pts[1][1] - pts[0][1]),
          }));
        }
      } else if (it.type === 'ink') {
        if (pts.length >= 2) {
          g.append(el('path', { d: pts.map((p, i) => `${i ? 'L' : 'M'} ${p[0]} ${p[1]}`).join(' ') }));
        }
      } else if (it.type === 'text') {
        // 文字錨點畫一個小十字，才看得出它釘在哪
        if (pts[0]) {
          const [x, y] = pts[0];
          const r = 4 * upp;
          g.append(el('path', { d: `M ${x - r} ${y} L ${x + r} ${y} M ${x} ${y - r} L ${x} ${y + r}`, stroke: it.color }));
        }
      }
      // 文字（雲狀圖／箭頭／文字都可以帶說明）
      if (it.text) {
        const [x, y] = pts[0] ?? [0, 0];
        const t = el('text', {
          x: x + 6 * upp, y: y - 6 * upp, fill: it.color, stroke: 'none',
          'font-size': font, 'data-text': '1',
        });
        t.textContent = it.text;
        g.append(t);
      }
      // 編號徽章：跟右側議題清單對得起來
      if (!draft) {
        const idx = this.items.indexOf(it) + 1;
        if (idx > 0) {
          const p = pts[0] ?? [0, 0];
          const r = 9 * upp;
          g.append(el('circle', {
            cx: p[0], cy: p[1] - r * 2.4, r, fill: it.color, stroke: 'none', opacity: it.status === 'closed' ? 0.4 : 0.85,
          }));
          const tn = el('text', {
            x: p[0], y: p[1] - r * 2.4 + r * 0.36, fill: '#fff', stroke: 'none',
            'font-size': 11 * upp, 'text-anchor': 'middle', 'font-weight': '700',
          });
          tn.textContent = String(idx);
          g.append(tn);
        }
      }
      this.g.append(g);
    };
    for (const it of this.items) draw(it);
    if (this._draft) draw({ ...this._draft, id: '__draft', color: this.color }, true);
  }

  /* ── 互動 ───────────────────────────────────────────────── */

  _bind() {
    this._onDown = (e) => this._down(e);
    this._onMove = (e) => this._move(e);
    this._onUp = (e) => this._up(e);
    this._onDbl = (e) => this._dbl(e);
    this._onKey = (e) => this._key(e);
    this.svg.addEventListener('pointerdown', this._onDown);
    this.svg.addEventListener('pointermove', this._onMove);
    this.svg.addEventListener('pointerup', this._onUp);
    this.svg.addEventListener('pointercancel', this._onUp);
    this.svg.addEventListener('dblclick', this._onDbl);
    window.addEventListener('keydown', this._onKey);
  }

  _pt(e) {
    const p = this.toLocal(e.clientX, e.clientY);
    return p ? [Number(p[0].toFixed(4)), Number(p[1].toFixed(4))] : null;
  }

  _down(e) {
    this._lastClient = [e.clientX, e.clientY];
    if (!this.tool) {
      // 檢視模式：點到圖形就選它
      const hit = e.target.closest?.('.ann');
      if (hit) { this.select(hit.dataset.id); e.stopPropagation(); }
      else this.select(null);
      return;
    }
    const p = this._pt(e);
    if (!p) return;
    e.preventDefault();
    this.svg.setPointerCapture?.(e.pointerId);
    if (this.tool === 'text') { this._placeText(p); return; }
    if (this.tool === 'cloud') {
      // 逐點累積（雙擊／Enter 結束）
      //
      // 雙擊要**自己判斷**：`_down()` 呼叫了 `preventDefault()`，而取消
      // pointerdown 會讓瀏覽器不再送出相容滑鼠事件（含 dblclick）——
      // 依賴 dblclick 事件的話雲狀圖永遠結束不了（實測踩過）。所以這裡用
      // 「時間 + 距離」自己認雙擊，`dblclick` 監聽只當後備。
      const now = performance.now();
      const last = this._lastClick;
      this._lastClick = { t: now, p };
      if (last && now - last.t < 400
          && Math.hypot(p[0] - last.p[0], p[1] - last.p[1]) < 8 * this._unitsPerPx()) {
        this._commit(this._pending);
        this._lastClick = null;
        return;
      }
      this._pending = this._pending ?? { id: uid(), type: 'cloud', space: this.space, points: [], text: '', color: this.color, width: 2, status: 'open' };
      this._pending.points.push(p);
      this._draft = { ...this._pending };
      this.render();
      this._status(`雲狀圖：已 ${this._pending.points.length} 點（雙擊或 Enter 完成）`);
      return;
    }
    this._lastClick = null;
    // 拖曳型：arrow / line / rect / ink
    this._drawing = true;
    this._pending = {
      id: uid(), type: this.tool, space: this.space, points: [p, p],
      text: '', color: this.color, width: 2, status: 'open',
    };
    this._draft = { ...this._pending };
    this.render();
  }

  _move(e) {
    if (!this._drawing || !this._pending) return;
    const p = this._pt(e);
    if (!p) return;
    if (this.tool === 'ink') {
      const last = this._pending.points[this._pending.points.length - 1];
      if (Math.hypot(p[0] - last[0], p[1] - last[1]) > 0) this._pending.points.push(p);
    } else {
      this._pending.points[this._pending.points.length - 1] = p;
    }
    this._draft = { ...this._pending };
    this.render();
  }

  _up(e) {
    if (!this._drawing || !this._pending) return;
    this._drawing = false;
    this.svg.releasePointerCapture?.(e.pointerId);
    this._commit(this._pending);
  }

  _dbl(e) {
    if (this.tool !== 'cloud' || !this._pending) return;
    e.preventDefault();
    // 雙擊會在 points 裡多留一個重複點，去掉
    const pts = this._pending.points;
    if (pts.length >= 2 && pts[pts.length - 1][0] === pts[pts.length - 2][0]
      && pts[pts.length - 1][1] === pts[pts.length - 2][1]) pts.pop();
    this._commit(this._pending);
  }

  _key(e) {
    if (e.key === 'Escape') {
      if (this._pending || this._draft) { this._pending = null; this._drawing = false; this._draft = null; this.render(); this._status('已取消'); return; }
      if (this.tool) { this.setTool(null); return; }
    }
    if (e.key === 'Enter' && this._pending?.type === 'cloud') { this._commit(this._pending); return; }
    if ((e.key === 'Delete' || e.key === 'Backspace') && this.selected && !this._typing()) {
      e.preventDefault();
      this.delete(this.selected);
    }
  }

  _typing() {
    const a = document.activeElement;
    return a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA');
  }

  _placeText(p) {
    const box = this.host.getBoundingClientRect();
    const input = document.createElement('input');
    input.className = 'ann-input';
    input.placeholder = '輸入說明，Enter 完成';
    // 輸入框用**螢幕座標**定位（標註本身的座標是圖面單位，不能直接當像素用）
    input.style.left = `${(this._lastClient?.[0] ?? box.left) - box.left}px`;
    input.style.top = `${(this._lastClient?.[1] ?? box.top) - box.top}px`;
    this.host.append(input);
    input.focus();
    let finished = false;
    const done = (commit) => {
      if (finished) return;          // Enter 之後緊接著的 blur 不能再收尾一次
      finished = true;
      const text = input.value.trim();
      input.remove();
      if (commit && text) {
        this._commit({
          id: uid(), type: 'text', space: this.space, points: [p], text,
          color: this.color, width: 2, status: 'open',
        });
      }
    };
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') done(true);
      if (e.key === 'Escape') done(false);
    });
    input.addEventListener('blur', () => done(true));
  }

  _commit(draft) {
    const it = { ...draft, id: draft.id === '__draft' ? uid() : draft.id };
    it.createdAt = new Date().toISOString();
    // 3D 標註要記住當時的視點，否則重開對不回同一個畫面
    if (it.space === 'screen' && this.viewpointProvider) {
      it.viewpoint = this.viewpointProvider() ?? null;
    }
    it.points = it.points.map((p) => [Number(p[0].toFixed(4)), Number(p[1].toFixed(4))]);
    const min = { cloud: 3, rect: 2, arrow: 2, line: 2, ink: 2, text: 1 }[it.type] ?? 1;
    const ok = it.points.length >= min;
    this._pending = null;
    this._draft = null;
    this._drawing = false;
    if (ok) {
      this.items.push(it);
      this.selected = it.id;
      this.onchange?.();
    } else {
      this._status(`點數不足（${TYPE_LABEL[it.type]}需要 ${min} 點），已取消`);
    }
    this.render();
    this.onselect?.(this.selected);
    if (ok) this._status('');
  }

  destroy() {
    if (this._raf) cancelAnimationFrame(this._raf);
    window.removeEventListener('keydown', this._onKey);
    window.removeEventListener('resize', this._onResize);
    this.svg.remove();
    this.svg = null;
  }
}
